import { useState, useEffect } from "react";
import { useLocation, useSearch } from "wouter";
import type { Template, Collection } from "@/api/client";
import { api } from "@/api/client";
import { TemplateCard } from "@/components/TemplateCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BaziForm } from "@/components/lifekline/BaziForm";
import type { BaziInput } from "@/lifekline/types";
import { ArrowLeft, Loader2, Sparkles, Folder } from "lucide-react";
import { Link } from "wouter";

export function CreateProjectPage() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const collectionId = params.get('collectionId');
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [collection, setCollection] = useState<Collection | null>(null);
  
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [name, setName] = useState("");
  const [inputText, setInputText] = useState("");
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      const response = await api.templates.list();
      if (response.success && response.data) {
        setTemplates(response.data);
        if (response.data && response.data.length > 0) {
          setSelectedTemplate(response.data[0]?.id ?? "");
        }
      }
      setLoadingTemplates(false);
    };

    fetchTemplates();
  }, []);

  // Fetch collection info if creating from collection
  useEffect(() => {
    if (collectionId) {
      const fetchCollection = async () => {
        const response = await api.collections.get(collectionId);
        if (response.success && response.data) {
          setCollection(response.data);
        }
      };
      fetchCollection();
    }
  }, [collectionId]);

  // Check if lifekline template is selected
  const isLifeklineSelected = selectedTemplate === 'lifekline';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTemplate) {
      setError("请选择一个模板");
      return;
    }

    // For lifekline, use BaziForm submission instead
    if (isLifeklineSelected) {
      return;
    }

    if (!inputText.trim()) {
      setError("请输入文本内容");
      return;
    }

    setSubmitting(true);

    const response = await api.projects.create({
      name: name.trim() || undefined,
      templateId: selectedTemplate,
      inputText: inputText.trim(),
      collectionId: collectionId || undefined,
    });

    if (response.success && response.data) {
      // If created from collection, redirect back to collection
      if (collectionId) {
        setLocation(`/collections/${collectionId}`);
      } else {
        setLocation(`/projects/${response.data.id}`);
      }
    } else {
      setError(response.error || "创建项目失败");
      setSubmitting(false);
    }
  };

  // Handle lifekline form submission
  const handleBaziSubmit = async (baziInput: BaziInput) => {
    setError(null);
    setSubmitting(true);

    const response = await api.projects.create({
      name: baziInput.name ? `${baziInput.name}的人生K线` : "人生K线分析",
      templateId: 'lifekline',
      inputText: JSON.stringify(baziInput),
      baziInput: baziInput,
      collectionId: collectionId || undefined,
    });

    if (response.success && response.data) {
      setLocation(`/projects/${response.data.id}`);
    } else {
      setError(response.error || "创建项目失败");
      setSubmitting(false);
    }
  };

  // Back link - go to collection if from collection, otherwise home
  const backLink = collectionId ? `/collections/${collectionId}` : "/";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Link href={backLink}>
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-xl font-bold text-foreground">创建新项目</h1>
            {collection ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Folder className="w-3.5 h-3.5 text-violet-500" />
                <span>添加到合集: {collection.name}</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">选择模板并输入内容</p>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Template Selection */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-4">
              选择模板
            </h2>
            
            {loadingTemplates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={selectedTemplate === template.id}
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Lifekline specific form */}
          {isLifeklineSelected ? (
            <section className="bg-gradient-to-br from-violet-500/5 to-amber-500/5 rounded-2xl border border-violet-200 dark:border-violet-900 p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                🔮 八字信息录入
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                请输入您的八字信息，AI 将为您生成人生运势K线图和详细分析报告
              </p>
              <BaziForm onSubmit={handleBaziSubmit} isSubmitting={submitting} />
            </section>
          ) : (
            <>
              {/* Project Name */}
              <section>
                <Label htmlFor="name" className="text-lg font-semibold">
                  项目名称 <span className="text-sm font-normal text-muted-foreground">(选填)</span>
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">
                  不填写将由 AI 自动生成名称
                </p>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="为您的项目起个名字..."
                />
              </section>

              {/* Input Text */}
              <section>
                <Label htmlFor="inputText" className="text-lg font-semibold">
                  输入文本
                </Label>
                <p className="text-sm text-muted-foreground mt-1 mb-2">
                  AI 将分析您的文本并生成相关图片
                </p>
                <textarea
                  id="inputText"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="输入您想要转化为图片的文本内容...&#10;&#10;例如：描述一个宁静的夏日午后，阳光透过树叶洒在草地上，一只蝴蝶停在盛开的花朵上..."
                  rows={8}
                  className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm shadow-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
              </section>
            </>
          )}

          {/* Error message */}
          {error && (
            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Submit - only show for non-lifekline templates */}
          {!isLifeklineSelected && (
            <div className="flex items-center gap-4">
              <Button
                type="submit"
                size="lg"
                disabled={submitting || loadingTemplates}
                className="gap-2"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Sparkles className="w-5 h-5" />
                )}
                {submitting ? "创建中..." : "开始生成"}
              </Button>
              
              <Link href={backLink}>
                <Button type="button" variant="outline" size="lg">
                  取消
                </Button>
              </Link>
            </div>
          )}
        </form>
      </main>
    </div>
  );
}

